import type { CustomChart } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import type { GranularityType } from "@tokenterminal/tt-analytics-api-types/dist/utils/granularity"
import { type Atom, atom, useAtomValue } from "jotai"
import { Suspense, useContext, useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { ErrorBoundary } from "../../ui/ErrorBoundary"
import { GeneralError } from "../../ui/GeneralError"
import { BaseChart } from "./components/chart/BaseChart"
import { ChartHeading } from "./components/chart/ChartHeading"
import { Legend } from "./components/legend/Legend"
import { LegendPlaceholder } from "./components/legend/Placeholder"
import { useSeries } from "./hooks/useSeries"
import { ChartContext } from "./tt-ui-chart/ChartContext"
import { BarSkeleton } from "./tt-ui-chart/ChartLoadingIndicator"

function logCoreChartDebug(
  chartTitle: string,
  stage: string,
  details?: Record<string, unknown>
) {
  console.debug("[core-chart]", {
    chartTitle,
    stage,
    ...details,
  })
}

type BaseChartProps = {
  showTitle: boolean
  showDescription: boolean
  isVisible: boolean
  onVisibilityChange?: (
    id: string,
    isVisible: boolean,
    legendItemIds?: string[]
  ) => void
  showLegend?: boolean
  hideYAxisLabels?: boolean
  headingImageSlot?: React.ReactNode
  includeAllChains?: boolean
  menuSlot?: React.ReactNode
  showProductSymbol?: boolean
  includeProductsInProjectBreakdown?: boolean
  /** Drop the redundant " (Project)" suffix on product-grouped series labels
   * when the chart is already scoped to a single project. Off by default. */
  hideProductProjectSuffix?: boolean
  className?: string
  headingHref?: string
  disableClickToOpenModal?: boolean
  /** Let Highcharts reduce the rendered point count to the available width. */
  enableDataGrouping?: boolean
}

// New: Plain value props (preferred)
type ChartPropsWithConfig = BaseChartProps & {
  /** Chart configuration as plain value (preferred) */
  config: CustomChart
  /** @deprecated Use `config` instead */
  configAtom?: never
}

// Legacy: Atom props (deprecated, for backward compatibility)
type ChartPropsWithAtom = BaseChartProps & {
  /** @deprecated Use `config` instead */
  configAtom: Atom<CustomChart>
  config?: never
}

export type ChartProps = ChartPropsWithConfig | ChartPropsWithAtom

/**
 * Hook to resolve config from either plain value or atom.
 * Allows gradual migration from atom-based to value-based props.
 */
function useChartConfig(props: ChartProps): CustomChart {
  // Type guard to check if using atom
  const isAtomProps = (p: ChartProps): p is ChartPropsWithAtom =>
    "configAtom" in p && p.configAtom !== undefined

  // For atom props, use useAtomValue (must be called unconditionally)
  const atomValue = useAtomValue(
    isAtomProps(props) ? props.configAtom : nullAtom
  )

  if (isAtomProps(props)) {
    return atomValue as CustomChart
  }

  return props.config
}

// Dummy atom that returns null - used when config prop is provided
const nullAtom = atom<CustomChart | null>(null)

export function Chart(props: ChartProps) {
  const {
    showTitle,
    showDescription,
    isVisible = true,
    onVisibilityChange,
    showLegend = true,
    hideYAxisLabels = false,
    headingImageSlot,
    includeAllChains = true,
    menuSlot,
    showProductSymbol = false,
    includeProductsInProjectBreakdown = false,
    hideProductProjectSuffix = false,
    className,
    headingHref,
    disableClickToOpenModal = false,
    enableDataGrouping = false,
  } = props

  const chartSerieSettings = useChartConfig(props)
  const [error, setError] = useState<Error | null>(null)
  const [isChartReady, setIsChartReady] = useState(false)
  const isLoadingRef = useRef(false)
  const { events } = useContext(ChartContext)

  const { series, seriesData, isLoading } = useSeries({
    chartSerieSettings: chartSerieSettings.configs,
    zoom: chartSerieSettings.zoom,
    granularity: chartSerieSettings.granularity as GranularityType,
    showCurrentPeriod: chartSerieSettings.show_current_period,
    includeAllChains,
    start: chartSerieSettings?.start,
    end: chartSerieSettings?.end,
    showProductSymbol,
    includeProductsInProjectBreakdown,
    hideProductProjectSuffix,
    suspense: false,
  })

  isLoadingRef.current = isLoading

  useEffect(() => {
    logCoreChartDebug(chartSerieSettings.title ?? "untitled", "mount", {
      seriesConfigCount: chartSerieSettings.configs.length,
    })
  }, [chartSerieSettings.title, chartSerieSettings.configs.length])

  useEffect(() => {
    logCoreChartDebug(chartSerieSettings.title ?? "untitled", "data-state", {
      isLoading,
      isChartReady,
      seriesCount: series.length,
      nonEmptySeriesCount: Object.values(seriesData).filter(
        (data) => data.length > 0
      ).length,
      totalSeriesKeys: Object.keys(seriesData).length,
      isVisible,
    })
  }, [
    chartSerieSettings.title,
    isLoading,
    isChartReady,
    series,
    seriesData,
    isVisible,
  ])

  useEffect(() => {
    if (isLoading) {
      logCoreChartDebug(
        chartSerieSettings.title ?? "untitled",
        "reset-chart-ready",
        {
          reason: "series-loading",
        }
      )
      setIsChartReady(false)
    }
  }, [chartSerieSettings.title, isLoading])

  useEffect(() => {
    const markChartReady = (eventType: "load" | "render" | "redraw") => {
      logCoreChartDebug(
        chartSerieSettings.title ?? "untitled",
        "highcharts-event",
        {
          eventType,
          isLoading: isLoadingRef.current,
        }
      )

      if (!isLoadingRef.current) {
        logCoreChartDebug(
          chartSerieSettings.title ?? "untitled",
          "set-chart-ready",
          {
            eventType,
          }
        )
        setIsChartReady(true)
      }
    }

    const handleLoad = () => markChartReady("load")
    const handleRender = () => markChartReady("render")
    const handleRedraw = () => markChartReady("redraw")

    events.add("load", handleLoad)
    events.add("render", handleRender)
    events.add("redraw", handleRedraw)

    return () => {
      events.remove("load", handleLoad)
      events.remove("render", handleRender)
      events.remove("redraw", handleRedraw)
    }
  }, [chartSerieSettings.title, events])

  const showLoadingSkeleton = !error && (isLoading || !isChartReady)

  useEffect(() => {
    logCoreChartDebug(
      chartSerieSettings.title ?? "untitled",
      "skeleton-state",
      {
        showLoadingSkeleton,
        isLoading,
        isChartReady,
        hasError: !!error,
      }
    )
  }, [
    chartSerieSettings.title,
    error,
    isChartReady,
    isLoading,
    showLoadingSkeleton,
  ])

  return (
    <div className="flex flex-col gap-4 h-full">
      {(showTitle || showDescription || showLegend) && (
        <div
          className={twMerge(
            "flex flex-col",
            (showDescription || showTitle) && "gap-4"
          )}
        >
          <ChartHeading
            showTitle={showTitle}
            showDescription={showDescription}
            title={chartSerieSettings.title ?? ""}
            description={chartSerieSettings.description ?? ""}
            headingImageSlot={headingImageSlot}
            menuSlot={menuSlot}
            headingHref={headingHref}
          />
          {showLegend && !error && (
            <Suspense fallback={<LegendPlaceholder />}>
              <Legend
                onVisibilityChange={onVisibilityChange}
                chartSerieSettings={chartSerieSettings}
                series={series}
              />
            </Suspense>
          )}
        </div>
      )}
      <div className={twMerge("relative min-h-0 grow", className)}>
        {showLoadingSkeleton && (
          <BarSkeleton
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        )}
        <div
          className={twMerge(
            "h-full",
            // Transition only on reveal: hiding must be instant so stale
            // content (e.g. a transient empty state) never fades out visibly.
            showLoadingSkeleton
              ? "opacity-0 pointer-events-none"
              : "transition-opacity duration-200 ease-out"
          )}
        >
          <ErrorBoundary fallback={<GeneralError />} onError={setError}>
            <BaseChart
              isVisible={isVisible}
              chartSerieSettings={chartSerieSettings}
              series={series}
              seriesData={seriesData}
              hideYAxisLabels={hideYAxisLabels}
              disableClickToOpenModal={disableClickToOpenModal}
              enableDataGrouping={enableDataGrouping}
            />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}
